import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import PostsShellLayout from '../../newsintelligence/support/openDiscussions/components/shell/PostsShellLayout';
import { usePostsShellLayout } from '../../newsintelligence/support/openDiscussions/hooks/usePostsShellLayout';
import { useNotification } from '../ReusableComponent/PopupNotification/PopupNotification';
import '../../GlobalPosts/PostsPage.css';

/**
 * Reusable app shell using the same header + left sidebar chrome as /posts.
 *
 * @param {Object} props
 * @param {Array} [props.sidebarItems] - Custom nav items (posts-styled AppShellSidebar); omit for posts nav
 * @param {React.ReactNode} [props.sidebar] - Fully custom sidebar node (overrides sidebarItems)
 * @param {Object} [props.sidebarProps] - Extra props merged onto PostsShellSidebar when using default posts nav
 * @param {string} [props.layoutClassName] - Extra class on the outer posts-page-layout wrapper
 * @param {string} [props.mainClassName] - Class on the main content column
 * @param {string} [props.searchPlaceholder] - Header search placeholder
 * @param {boolean} [props.showHeaderSearch] - Show header search (default true)
 * @param {string} [props.returnTo] - Return URL for guest sign-in / header auth
 * @param {boolean} [props.messagesStandalone] - Messages-only header (no feed search / social tools)
 * @param {React.ReactNode | ((shell: object) => React.ReactNode)} [props.children] - Main content (falls back to <Outlet />)
 */
export default function MainLayoutTemplate({
  sidebarItems = null,
  sidebar = null,
  sidebarProps = {},
  layoutClassName = '',
  mainClassName = 'posts-page-layout__center',
  searchPlaceholder,
  showHeaderSearch = true,
  returnTo,
  messagesStandalone = false,
  onManageProfileClick,
  children = null,
}) {
  const location = useLocation();
  const { addNotification } = useNotification();
  const isAppShell = Boolean(sidebarItems?.length || sidebar);
  const shell = usePostsShellLayout({
    returnTo: returnTo ?? `${location.pathname}${location.search}`,
    addNotification,
    appShell: isAppShell,
  });

  const mainContent = typeof children === 'function' ? children(shell) : children ?? <Outlet />;

  return (
    <PostsShellLayout
      shell={shell}
      sidebar={sidebar}
      sidebarItems={sidebarItems}
      sidebarProps={sidebarProps}
      layoutClassName={layoutClassName}
      mainClassName={mainClassName}
      searchPlaceholder={searchPlaceholder}
      showHeaderSearch={showHeaderSearch}
      returnTo={returnTo}
      appShell={isAppShell}
      messagesStandalone={messagesStandalone}
      onManageProfileClick={onManageProfileClick}
    >
      {mainContent}
    </PostsShellLayout>
  );
}
